<template>
  <a-modal
    :visible="visible"
    :confirm-loading="loading"
    :footer="null"
    @cancel="handleCancel"
  >
    <template #title>
      <div class="modal-title">{{ isFolder ? '폴더 이름 변경' : '파일 이름 변경' }}</div>
    </template>
    
    <div class="modal-content">
      <a-form
        ref="renameForm"
        :model="formState"
        :rules="rules"
        layout="vertical"
      >
        <a-form-item :label="isFolder ? '현재 폴더명' : '현재 파일명'">
          <a-input
            :value="currentFileName"
            disabled
          />
        </a-form-item>

        <a-form-item 
          :label="isFolder ? '새 폴더명' : '새 파일명'" 
          name="newFileName"
        >
          <a-input
            v-model:value="formState.newFileName"
            :placeholder="isFolder ? '새 폴더명을 입력하세요' : '새 파일명을 입력하세요'"
            allow-clear
          />
        </a-form-item>
      </a-form>

      <div class="modal-footer">
        <a-button @click="handleCancel">취소</a-button>
        <a-button 
          type="primary" 
          :loading="loading" 
          @click="handleSubmit"
        >변경</a-button>
      </div>
    </div>
  </a-modal>
</template>

<script>
import { defineComponent } from 'vue';
import { message } from 'ant-design-vue';

export default defineComponent({
  name: 'RenameFileModal',

  props: {
    visible: {
      type: Boolean,
      required: true
    },
    currentFileName: {
      type: String,
      default: ''
    },
    isFolder: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      loading: false,
      formState: {
        newFileName: ''
      },
      fileExtension: ''
    }
  },
  
  computed: {
    rules() {
      return {
        newFileName: [
          { required: true, message: this.isFolder ? '새 폴더명을 입력해주세요' : '새 파일명을 입력해주세요' }
        ]
      };
    }
  },

  methods: {
    async validateForm() {
      try {
        await this.$refs.renameForm.validate();
        return true;
      } catch (error) {
        return false;
      }
    },

    async handleSubmit() {
      const isValid = await this.validateForm();
      if (!isValid) return;

      if (!this.formState.newFileName) {
        message.warning('새 파일명을 입력해주세요.');
        return;
      }

      try {
        this.loading = true;
        
        this.$emit('rename', this.formState.newFileName + this.fileExtension);
      } catch (error) {
        message.error('파일 이름 변경 중 오류가 발생했습니다.');
      } finally {
        this.loading = false;
      }
    },

    handleCancel() {
      this.$emit('update:visible', false);
      this.resetState();
    },
    
    resetState() {
      this.formState.newFileName = '';
      this.loading = false;
      this.fileExtension = '';
    }
  },
  
  watch: {
    visible(newVal) {
      if (!newVal) {
        this.resetState();
      } else {
        this.resetState();
        if (this.currentFileName) {
          const name = this.currentFileName;
          if (!this.isFolder && name.includes('.')) {
            const dotIndex = name.lastIndexOf('.');
            this.formState.newFileName = name.substring(0, dotIndex);
            this.fileExtension = name.substring(dotIndex);
          } else {
            this.formState.newFileName = name;
            this.fileExtension = '';
          }
        }
      }
    },
    
    currentFileName() {
      this.resetState();
    }
  },

  mounted() {
    if (this.currentFileName) {
      const name = this.currentFileName;
      if (!this.isFolder && name.includes('.')) {
        const dotIndex = name.lastIndexOf('.');
        this.formState.newFileName = name.substring(0, dotIndex);
        this.fileExtension = name.substring(dotIndex);
      } else {
        this.formState.newFileName = name;
        this.fileExtension = '';
      }
    }
  }
});
</script>

<style lang="scss" scoped>
.modal-title {
  font-size: 16px;
  font-weight: bold;
}

.modal-content {
  padding: 20px;
  border: none;
  background: #fff;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 24px;
}

:deep(.ant-modal) {
  padding-bottom: 0;
  
  .ant-modal-content {
    border-radius: 8px;
    overflow: hidden;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);

    .ant-modal-close {
      color: rgba(0, 0, 0, 0.45);
    }

    .ant-modal-header {
      padding: 16px 20px;
      border: none;
      background: #fff;
      margin: 0;
    }

    .ant-modal-body {
      padding: 0;
      background: #fff;
      border: none;
    }

    .ant-modal-header,
    .ant-modal-body,
    .ant-modal-footer {
      border: none;
    }

    .ant-form-item {
      margin-bottom: 24px;
      
      &:last-child {
        margin-bottom: 0;
      }
    }
  }
}
</style> 